import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Plus, Search, ExternalLink, Globe, Lock, Heart, Star, CheckCircle, Filter, Info, Bookmark } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import HelpButton from '../components/HelpButton';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const EventsPage = () => {
  const { isAuthenticated, demoMode } = useAuth();
  const canContribute = isAuthenticated && !demoMode;
  
  const [events, setEvents] = useState([]);
  const [eventsByCategory, setEventsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationSearch, setLocationSearch] = useState('');
  const [radiusFilter, setRadiusFilter] = useState(100);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [externalResources, setExternalResources] = useState([]);
  const [showResources, setShowResources] = useState(false);
  const [categories, setCategories] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Category icons and colors
  const categoryStyles = {
    inclusive_social: { icon: '👥', color: 'bg-blue-100 text-blue-800', borderColor: 'border-blue-300' },
    sensory_friendly: { icon: '🎧', color: 'bg-purple-100 text-purple-800', borderColor: 'border-purple-300' },
    sensory_certified: { icon: '✓', color: 'bg-green-100 text-green-800', borderColor: 'border-green-300' },
    recurring_programs: { icon: '🎬', color: 'bg-orange-100 text-orange-800', borderColor: 'border-orange-300' },
    community_meetups: { icon: '💬', color: 'bg-teal-100 text-teal-800', borderColor: 'border-teal-300' }
  };

  const eventTypes = {
    all: 'All Events',
    sensory_friendly: 'Sensory-Friendly',
    adaptive_sports: 'Adaptive Sports',
    educational: 'Educational',
    support_group: 'Support Groups',
    social: 'Social'
  };

  // Load events on mount
  useEffect(() => {
    loadEvents();
  }, []);

  const formatDate = (date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventWebsiteUrl = (event) => {
    if (!event.website) return null;
    try {
      const url = new URL(event.website);
      return `${url.protocol}//${url.hostname}`;
    } catch (e) {
      return event.website;
    }
  };

  const getOrganizationName = (event) => {
    if (!event.website) return event.source || 'Event Organizer';
    try {
      const url = new URL(event.website);
      return url.hostname.replace('www.', '').split('.')[0];
    } catch (e) {
      return event.source || 'Event Organizer';
    }
  };

  const loadEvents = async (zipCode = '55401') => {
    setLoading(true);
    try {
      // Try the new multi-source events API first
      const response = await axios.get(`${API_URL}/api/inclusive-events/search`, {
        params: {
          zip_code: zipCode,
          radius_miles: radiusFilter,
          include_national: true
        }
      });
      
      if (response.data) {
        const transformedEvents = (response.data.events || []).map(event => ({
          ...event,
          start_time: new Date(event.start_time),
          end_time: new Date(event.end_time),
          attendee_count: Math.floor(Math.random() * 50) + 10
        }));
        
        setEvents(transformedEvents);
        setEventsByCategory(response.data.events_by_category || {});
        setExternalResources(response.data.external_resources || []);
        setCategories(response.data.categories || {});
      }
    } catch (error) {
      console.log('Multi-source API not available, falling back:', error.message);
      // Fallback to old API
      try {
        const fallbackResponse = await axios.get(`${API_URL}/api/events/accessible/search`, {
          params: { zip_code: zipCode, radius_miles: radiusFilter }
        });
        
        if (fallbackResponse.data?.events) {
          const transformedEvents = fallbackResponse.data.events.map(event => ({
            ...event,
            start_time: new Date(event.start_time),
            end_time: new Date(event.end_time),
            attendee_count: Math.floor(Math.random() * 50) + 10
          }));
          setEvents(transformedEvents);
        }
      } catch (fallbackError) {
        console.error('All event APIs failed:', fallbackError);
        setEvents([]);
      }
    }
    setLoading(false);
  };

  const handleLocationSearch = () => {
    const zipCode = locationSearch.match(/^\d{5}$/) ? locationSearch : '55401';
    loadEvents(zipCode);
  };

  // Filter events based on selected filters
  const getFilteredEvents = () => {
    let filtered = events;
    
    if (filter !== 'all') {
      filtered = filtered.filter(event => event.event_type === filter);
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(event => event.category === categoryFilter);
    }
    
    return filtered;
  };

  const filteredEvents = getFilteredEvents();

  // Render category badge
  const CategoryBadge = ({ category }) => {
    const style = categoryStyles[category] || categoryStyles.inclusive_social;
    const categoryName = categories[category]?.name || category;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.color}`}>
        <span className="mr-1">{style.icon}</span>
        {categoryName}
      </span>
    );
  };

  // Render event card
  const EventCard = ({ event }) => {
    const style = categoryStyles[event.category] || categoryStyles.inclusive_social;
    
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border-l-4 ${style.borderColor} p-5 hover:shadow-md transition-shadow cursor-pointer`}
        onClick={() => setSelectedEvent(event)}
        data-testid={`event-${event.id}`}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{event.title}</h3>
            <CategoryBadge category={event.category} />
          </div>
          {event.kulturecity_certified && (
            <span className="flex items-center text-green-600 text-xs font-medium bg-green-50 px-2 py-1 rounded-full">
              <CheckCircle size={12} className="mr-1" />
              Certified
            </span>
          )}
        </div>

        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{event.description}</p>

        <div className="space-y-1.5 mb-3">
          <div className="flex items-center text-sm text-gray-600">
            <Calendar size={14} className="mr-2 text-gray-400" />
            {formatDate(event.start_time)}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Clock size={14} className="mr-2 text-gray-400" />
            {formatTime(event.start_time)} - {formatTime(event.end_time)}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <MapPin size={14} className="mr-2 text-gray-400" />
            {event.venue_name}
          </div>
          {event.distance_miles !== undefined && event.distance_miles > 0 && (
            <div className="text-sm text-blue-600 font-medium">
              {event.distance_miles} miles away
            </div>
          )}
          {event.national && (
            <div className="text-sm text-orange-600 flex items-center">
              <Globe size={14} className="mr-1" />
              National program - check local venue
            </div>
          )}
        </div>

        {/* Accessibility features */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(event.accessibility_features || []).slice(0, 4).map((feature) => (
            <span
              key={feature}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
            >
              {feature.replace(/_/g, ' ')}
            </span>
          ))}
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <span className="text-sm text-gray-500">{event.cost || 'Check website'}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedEvent(event);
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View Details →
          </button>
        </div>
      </div>
    );
  };

  // External Resources Section
  const ResourcesSection = () => (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Bookmark size={20} className="mr-2 text-blue-600" />
            Find More Events
          </h2>
          <p className="text-gray-600 text-sm">Explore these trusted resources for inclusive events in your area</p>
        </div>
        <button
          onClick={() => setShowResources(!showResources)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {showResources ? 'Hide' : 'Show All'}
        </button>
      </div>
      
      <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-4 ${showResources ? '' : 'max-h-32 overflow-hidden'}`}>
        {externalResources.map((resource, index) => {
          const style = categoryStyles[resource.category] || categoryStyles.inclusive_social;
          return (
            <a
              key={index}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900 text-sm">{resource.name}</h3>
                <ExternalLink size={14} className="text-gray-400" />
              </div>
              <p className="text-gray-600 text-xs mb-2 line-clamp-2">{resource.description}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${style.color}`}>
                {style.icon} {categories[resource.category]?.name || resource.category}
              </span>
            </a>
          );
        })}
      </div>
      
      {!showResources && externalResources.length > 4 && (
        <div className="text-center mt-3">
          <button
            onClick={() => setShowResources(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Show {externalResources.length - 4} more resources ↓
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="events-title">
              Inclusive Events
            </h1>
            <p className="text-gray-600 mt-1">
              Sensory-friendly, adaptive, and inclusive events from multiple trusted sources
            </p>
          </div>
          {canContribute ? (
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]">
              <Plus size={20} className="mr-2" />
              Suggest Event
            </button>
          ) : (
            <button 
              disabled
              className="flex items-center px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed min-h-[44px]"
              title="Create an account to suggest events"
            >
              <Lock size={20} className="mr-2" />
              Suggest Event
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
          <div className="grid md:grid-cols-4 gap-4 mb-4">
            <div className="md:col-span-2">
              <label htmlFor="location-search" className="block text-sm font-medium text-gray-700 mb-1">
                Location (Zip Code)
              </label>
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="location-search"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
                  placeholder="Enter zip code (e.g., 55401, 90210)"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label htmlFor="radius" className="block text-sm font-medium text-gray-700 mb-1">
                Radius
              </label>
              <select
                id="radius"
                value={radiusFilter}
                onChange={(e) => setRadiusFilter(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={25}>25 miles</option>
                <option value={50}>50 miles</option>
                <option value={100}>100 miles</option>
                <option value={200}>200+ miles</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleLocationSearch}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[42px]"
              >
                Search Events
              </button>
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Event Categories</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Categories
              </button>
              {Object.entries(categories).map(([key, cat]) => {
                const style = categoryStyles[key] || categoryStyles.inclusive_social;
                return (
                  <button
                    key={key}
                    onClick={() => setCategoryFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center ${
                      categoryFilter === key
                        ? 'bg-gray-800 text-white'
                        : `${style.color} hover:opacity-80`
                    }`}
                  >
                    <span className="mr-1">{style.icon}</span>
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(eventTypes).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    filter === key
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* External Resources */}
        {externalResources.length > 0 && <ResourcesSection />}

        {/* Events List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner text="Finding inclusive events..." />
          </div>
        ) : (
          <div>
            {/* Results count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                Found <span className="font-semibold text-gray-900">{filteredEvents.length}</span> events
                {categoryFilter !== 'all' && ` in ${categories[categoryFilter]?.name || categoryFilter}`}
              </p>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search filters or check out our external resources above
                </p>
                <button
                  onClick={() => {
                    setFilter('all');
                    setCategoryFilter('all');
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Event Details Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-gray-200 z-10">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedEvent.title}</h2>
                  <div className="mt-1">
                    <CategoryBadge category={selectedEvent.category} />
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2"
                  aria-label="Close modal"
                >
                  ✕
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* KultureCity badge if applicable */}
                {selectedEvent.kulturecity_certified && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                    <CheckCircle size={20} className="text-green-600 mr-2" />
                    <div>
                      <span className="font-medium text-green-800">KultureCity Certified</span>
                      <p className="text-green-700 text-sm">This venue is certified sensory-inclusive</p>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">About This Event</h3>
                  <p className="text-gray-700">{selectedEvent.description}</p>
                </div>

                {/* When & Where */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">When & Where</h3>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-start">
                        <Calendar size={16} className="mr-2 text-gray-400 mt-0.5" />
                        <div>
                          <div className="font-medium">{formatDate(selectedEvent.start_time)}</div>
                          <div>{formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}</div>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <MapPin size={16} className="mr-2 text-gray-400 mt-0.5" />
                        <div>
                          <div className="font-medium">{selectedEvent.venue_name}</div>
                          <div>{selectedEvent.address}</div>
                        </div>
                      </div>
                      {selectedEvent.recurrence && (
                        <div className="flex items-center text-blue-600">
                          <Info size={14} className="mr-2" />
                          <span className="text-sm">{selectedEvent.recurrence}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Accessibility Features</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedEvent.accessibility_features || []).map((feature) => (
                        <span
                          key={feature}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          ✓ {feature.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Cost:</span>
                      <span className="ml-2 font-medium text-gray-900">{selectedEvent.cost || 'Check website'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Source:</span>
                      <span className="ml-2 font-medium text-gray-900">{selectedEvent.source}</span>
                    </div>
                    {selectedEvent.distance_miles !== undefined && selectedEvent.distance_miles > 0 && (
                      <div>
                        <span className="text-gray-500">Distance:</span>
                        <span className="ml-2 font-medium text-blue-600">{selectedEvent.distance_miles} miles</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                  {getEventWebsiteUrl(selectedEvent) && (
                    <a
                      href={getEventWebsiteUrl(selectedEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                    >
                      <Globe size={18} className="mr-2" />
                      Visit {getOrganizationName(selectedEvent)}
                      <ExternalLink size={14} className="ml-2" />
                    </a>
                  )}
                  <button
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedEvent.venue_name + ' ' + selectedEvent.address)}`, '_blank')}
                    className="flex-1 flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  >
                    <MapPin size={18} className="mr-2" />
                    Get Directions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Help Button */}
        <HelpButton pageName="events" />
      </div>
    </div>
  );
};

export default EventsPage;
